import { Component, Input, ViewChild } from '@angular/core';
import { ContextMenuElement, MainMenuElement } from '@base/classes/BaseClasses';
import { EnumSystemOptions } from '@core/framework/systemoptions.enum';
import { Utils } from '@core/helpers/Utils';
import { BaseComponent } from '@m4u-angular/base/component/BaseComponent';
import { ServiceBagService } from '@m4u-angular/base/component/ServiceBag';
import { BaseResponse, BaseState } from '@m4u-angular/base/entities/BaseEntities';
import { DxTreeViewComponent } from 'devextreme-angular/ui/tree-view';
import { VersionDescriptor } from '@m4u-angular/configuration/app.config.service';
import { LoginEndpoint } from '@m4u-angular/core/endpoints/login.endpoint';
import { EnumLanguages, EnumMenuPositions, EnumMessageBoxTypes } from '@m4u-angular/core/framework/sysenums.enum';
import { Subscription } from 'rxjs';
import { LanguageService } from '@m4u-angular/core/framework/language.service';
import { RouteHistoryService } from '@m4u-angular/core/framework/route-history.service';
import { MessageBoxDialogComponent } from '@m4u-angular/ui/dialogs/messagebox-dialog/messageboxdialog.component';

class MenuTooltip {
    Target: Element;
    Content: string;
    Visible: boolean;
}

class State extends BaseState {   
    ModulesVersion: VersionDescriptor[] = [];
    MenuItems: MainMenuElement[] = [];
    MenuTooltip: MenuTooltip = new MenuTooltip;
}

@Component({
    selector: 'm4u-navmenu',
    templateUrl: './navmenu.component.html',
    styleUrls: ['./navmenu.component.scss']
})
export class NavMenuComponent extends BaseComponent<State> {
    @ViewChild(DxTreeViewComponent, { static: true }) menu: DxTreeViewComponent;
    @ViewChild('messageBox') messageBox: MessageBoxDialogComponent;

    private _menuOpened = false;
    @Input() set MenuOpened(value) {
        if (value === this._menuOpened) return;

        this._menuOpened = value;
        if (!this.MenuInstance) return;
        if (!value) this.MenuInstance.collapseAll();
        else this.ExpandParentItems();
        //if (value) this.InitializeItems();
    }
    get MenuOpened() {
        return this._menuOpened;
    }

    private get MenuInstance() {
        const menuInstance = this.menu && this.menu.instance;
        if (menuInstance && !menuInstance['_contentReadyAction']) menuInstance['_initContentReadyAction']();
        return menuInstance;
    }

    UserMenuItems: ContextMenuElement[];
    private _selectedItem: string;
    private _subscriptions: Subscription[] = [];

    constructor(A: ServiceBagService,
        private loginEndpoint: LoginEndpoint,
        private languageService: LanguageService,
        private routeHistoryService: RouteHistoryService) {
        super(A, 'NavMenuComponent', State);
    }

    override OnInitComponent() {
        this.InitMenuItems();
        this.UserMenuItems = this.GetUserMenuItems();

        this._subscriptions.push(this.A.SysEvents.MenuItems.subscribe((isNeedToReload) => {
            if (isNeedToReload) {
                this.InitializeItems();
                this.MenuInstance && this.MenuInstance.repaint();
            }
        }));
        
        this.State.ModulesVersion = this.A.AppConfigService.getModulesVersion();
    }

    override OnInitComponentFinished() {
        this._subscriptions.push(this.A.SysEvents.SelectedRoute.subscribe(selectedRoute => {
            this._selectedItem = selectedRoute;
            this.MenuInstance && this.MenuInstance.selectItem(selectedRoute);
        }));
    }

    InitMenuItems() {
        this._subscriptions.push(this.A.AppConfigService.AppConfig.OnGetMenu(this.A.Session.UserInfoDict)
            .subscribe(items => {
                this.State.MenuItems = items;
                this.InitializeItems();
            })
        );
    }

    ToggleMenu() {
        this.MenuOpened = !this.MenuOpened;
        this.A.SysEvents.SetMenuPositon(this.MenuOpened ? EnumMenuPositions.Open : EnumMenuPositions.Close);
    }

    GetUserMenuItems(): any[] {
        var showPasswordChangeLink = this.A.Framework.SystemOptionFlag(EnumSystemOptions.IsSupportedPasswordChange);
        return [{
                label: this.RC('btnChangePassword'),
                icon: 'mdi mdi-account-edit',
                visible: showPasswordChangeLink,
                command: this.OnChangePassword.bind(this)
            },{
                label: this.RC('btnLogout'),
                icon: 'mdi mdi-logout',
                visible: true,
                command: this.OnLogout.bind(this)
            }
        ];
    }

    IsUserMenuOpened(cm) {
        return cm?.containerViewChild?.nativeElement?.style?.display == 'block';
    }

    InitializeItems() {
        this.State.MenuItems.forEach(menu => this.InitializeItem(menu));
        this.MenuInstance && this.MenuInstance.option('items', this.State.MenuItems);
    }

    InitializeItem(menu: MainMenuElement) {
        menu.text = this.GetMenuTranslation(menu);
        menu.key = menu.moduleName + "." + menu.menu;
        this.InitMenuItem(menu);

        if (menu.items) menu.items.forEach(x => this.InitializeItem(x));
    }

    InitMenuItem(menu: MainMenuElement) {
        const menuUserInfo = menu.moduleName && this.A.Session.UserInfoDict[menu.moduleName];
        var permissionResult = Utils.IsEmpty(menu.permission) ? true : menuUserInfo && menuUserInfo.hasOwnProperty(menu.permission) && menuUserInfo[menu.permission];
        var permissionFunctionResult = Utils.IsEmpty(menu.permissionFunction) ? true : this.A.Session.UserInfos.some(x => x && menu.permissionFunction(x));
        menu.visible = !!permissionResult && permissionFunctionResult;

        // add 'rtl' class for custom icons in RTL
        if (this.A.Screen.IsRTL() && menu.icon && menu.icon.startsWith('m4u-ico')) menu.icon = `${menu.icon} rtl`;

        // set common icon for folders if it is empty
        if (menu.items && Utils.IsEmpty(menu.icon)) menu.icon = 'mdi mdi-folder-open';

        // hide main menu elements for mobile
        if (this.A.Screen.IsMobile()) menu.visible = menu.visible && (menu.isVisibleOnMobile == undefined ? true : menu.isVisibleOnMobile);

        if (this._selectedItem && this._selectedItem === menu.menu) {
            (menu as any).selected = true;
        }
    }

    ExpandParentItems() {
        if (!this.MenuInstance || !this._menuOpened) return;

        let parentMenus = this.GetParentMenus();
        parentMenus.forEach(x => this.MenuInstance.expandItem(x));
    }

    GetParentMenus() {
        const search = (parents, item: MainMenuElement): string[] => item.menu === this._selectedItem
            ? [...parents]
            : item.items?.length > 0 ? item.items.reduce((acc, child) => acc.concat(search([...parents, item.menu], child)), []) : []
        
        let parentMenus = [];
        this.State.MenuItems.filter(x => x.items?.length).forEach(x => {
            parentMenus = [...parentMenus, ...search([], x)];
        });
        return parentMenus;
    }

    GetMenuTranslation(menu: MainMenuElement) {
        return menu.text || this.A.ResourcesService.RC(`$$Pages.${menu.menu}`, menu.moduleName);
    }

    OnItemClick(event: any) {
        // Do nothing if menu was already selected
        if (event.node.selected) return;

        // If menu closed and it the highest level and it has children then just open it
        if (!this.MenuOpened && !event.node.parent && event.itemData.items) {
            this.A.SysEvents.SetMenuPositon(EnumMenuPositions.Open);
            return;
        }
        
        // Do nothing if menu path is empty
        const menu = event.itemData;
        if (!menu.path) return;

        const menuUserInfo = menu.moduleName ? this.A.Session.UserInfoDict[menu.moduleName] : this.A.Session.UserInfos[0];
        const pathParams = menu.pathParams && menu.pathParams(menuUserInfo) || [];

        this.routeHistoryService.ClearHistory();
        // Navigate my menu path and params
        this.A.Navigator.Navigate([menu.path, pathParams], menu.queryParams || null).then(() => {
            // Close menu after navigation
            if (this.MenuOpened) this.A.SysEvents.SetMenuPositon(EnumMenuPositions.Close);
        });
    }

    OnContentReady(e: any) {  
        let elements = e.component.element().querySelectorAll(".dx-treeview-item");    
        elements.forEach((el, index)  => {
            if (!this.State.MenuItems || !this.State.MenuItems[index]) return;
            
            el.addEventListener("mouseenter", event => {
                if (this.MenuOpened) return;

                const target = event.target as Element;
                this.State.MenuTooltip = {
                    Target: document.querySelector(`${Array.from(target.classList).map(x => `.${x}`).join('')} .menu-icon-container`),
                    Content: this.State.MenuItems[index]?.text,
                    Visible: !!target
                }
            });
            el.addEventListener("mouseleave", event => this.State.MenuTooltip = new MenuTooltip);
        });  
    }

    GetMenuIconElement(element: Element) {
        if (!element) return null;
        if (element.classList.contains('menu-icon-container') || !element.children || !element.children.length) return element;
        
        const r = Array.from(element.children).find(x => this.GetMenuIconElement(x));
        return r;
    }

    GetVersion(): string {
        return `S-${this.A.Framework.CompanyInfo.ServerVersion} : C-${Utils.NZ(this.A.Framework.CompanyInfo.ClientVersion)}`;
    }

    GetDisplayUserName(): string {
        if (this.A.Session.DisplayUserName == undefined) return "";
        return this.A.Session.DisplayUserName.substring(0, 25);
    }

    GetDisplayUserDescription(): string {
        return Utils.NZ(this.A.Session.DisplayUserDescription, this.RC('lblUser')).substring(0, 25);
    }

    OnShowUserMenu(event, cm) {
        event.preventDefault();
        event.stopPropagation();
        cm.show(event);
    }

    OnChangePassword() {
        this.Navigate(['/system/passwordchange']);
    }

    OnLogout() {
        this.messageBox.Type = EnumMessageBoxTypes.YesNo;
        this.messageBox.Prompt = this.RC('msgLogoutQuestion');
        this.messageBox.ShowDialogShort(() => { this.Navigate(['/system/logout']); });
    }

    GetAppTitle() {
        const moduleName = this.GetFirstModuleName();
        return this.A.ResourcesService.RC('AppTitles.General', moduleName);
    }

    GetFirstModuleName() {
        const moduleNames = this.A.AppConfigService.AppConfig.ModuleName.split(',');
        return moduleNames[0];
    }

    SwitchLanguage(language: EnumLanguages) {
        this.languageService.SetLanguage(language);
        this._subscriptions.push(this.loginEndpoint.ChangeLanguage(language).subscribe((response: BaseResponse) => {
            if (response.IsSuccess) window.location.reload();
        }));
    }

    override OnDestroyComponent() {
        this._subscriptions.forEach(x => x.unsubscribe());
    }
}
